import React, { useEffect, useState } from "react";
import { Plus, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import Layout, { PageHeader, Surface, StatusPill } from "@/components/Layout";
import { api } from "@/lib/api";
import { Field, Modal } from "@/pages/Merchants";

export default function ProviderConfigs() {
  const [items, setItems] = useState([]);
  const [env, setEnv] = useState("");
  const [open, setOpen] = useState(false);
  const [health, setHealth] = useState(null);
  const [form, setForm] = useState({ name: "tevau", base_url: "https://api.tevau.io/v1", api_key: "", api_secret: "", enabled: false, notes: "" });

  const load = async () => {
    const r = await api.get("/admin/provider-configs");
    setItems(r.data.items);
    setEnv(r.data.current_env);
  };
  const loadHealth = async () => {
    try {
      const r = await api.get("/admin/providers/health");
      setHealth(r.data);
    } catch (e) {
      setHealth({ error: e?.response?.data?.detail || "health probe failed" });
    }
  };
  useEffect(() => { load(); loadHealth(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/provider-configs", form);
      toast.success("Provider config saved");
      setOpen(false);
      setForm({ name: "tevau", base_url: "https://api.tevau.io/v1", api_key: "", api_secret: "", enabled: false, notes: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const disable = async (name) => {
    if (!window.confirm(`Disable provider '${name}'?`)) return;
    await api.delete(`/admin/provider-configs/${name}`);
    toast.success("Disabled");
    load();
  };

  return (
    <Layout>
      <div className="p-6 lg:p-10" data-testid="provider-configs-page">
        <PageHeader
          title="Provider Configuration"
          subtitle={`Active environment: ${env || "—"}`}
          actions={
            <button onClick={() => setOpen(true)} data-testid="add-provider-btn" className="h-9 px-4 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 transition-colors duration-150">
              <Plus size={14} weight="bold" /> New / update provider
            </button>
          }
        />

        <Surface className="p-5 mb-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 flex-1">
              <ShieldCheck size={20} className="text-emerald-600 mt-0.5" />
              <div>
                <div className="font-display text-lg tracking-tight font-medium">Sandbox-first architecture</div>
                <p className="text-sm text-zinc-600 mt-1">
                  Production credentials are encrypted at rest with AES-256-GCM (key derived from <code>JWT_SECRET</code> or <code>BLONIX_ENC_KEY</code>).
                  Switching <code>BLONIX_ENV</code> to <code>production</code> activates the live provider only when valid credentials are configured below.
                </p>
              </div>
            </div>
            <div data-testid="provider-health-card" className="min-w-[220px] bg-white border border-zinc-200 rounded-sm p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">Live health</div>
                <button onClick={loadHealth} data-testid="provider-health-refresh" className="text-xs text-zinc-700 hover:text-zinc-950">Refresh</button>
              </div>
              {!health ? (
                <div className="text-xs text-zinc-500">Probing…</div>
              ) : health.error ? (
                <div className="text-xs text-rose-600">{health.error}</div>
              ) : (
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-zinc-500">Provider</span><span className="font-mono">{health.name} ({health.mode})</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Env</span><span className="font-mono">{health.env}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Status</span><span className={health.health?.ok ? "text-emerald-700" : "text-rose-700"}>{health.health?.ok ? "OK" : (health.health?.status || "down")}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Latency</span><span className="font-mono">{health.health?.latency_ms ?? 0}ms</span></div>
                  {health.breaker && (
                    <div className="flex justify-between"><span className="text-zinc-500">Breaker</span><span className="font-mono">{health.breaker.state} ({health.breaker.failures} fails)</span></div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Surface>

        <Surface className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-3">Provider</th>
                <th className="px-5 py-3">Base URL</th>
                <th className="px-5 py-3">API key</th>
                <th className="px-5 py-3">Secret</th>
                <th className="px-5 py-3">Enabled</th>
                <th className="px-5 py-3">Updated</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.name} className="border-b border-zinc-50" data-testid={`provider-${p.name}`}>
                  <td className="px-5 py-3 font-medium">{p.name}</td>
                  <td className="px-5 py-3 text-xs font-mono text-zinc-600">{p.base_url || "—"}</td>
                  <td className="px-5 py-3"><StatusPill value={p.api_key_set ? "active" : "pending"} /></td>
                  <td className="px-5 py-3"><StatusPill value={p.api_secret_set ? "active" : "pending"} /></td>
                  <td className="px-5 py-3"><StatusPill value={p.enabled ? "active" : "suspended"} /></td>
                  <td className="px-5 py-3 text-xs text-zinc-500">{(p.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => disable(p.name)} data-testid={`disable-${p.name}`} className="text-xs px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-100 transition-colors duration-150">Disable</button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-xs text-zinc-500">No provider configurations. Sandbox is used by default.</td></tr>}
            </tbody>
          </table>
        </Surface>

        {open && (
          <Modal title="Provider configuration" onClose={() => setOpen(false)}>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">Provider</label>
                <select data-testid="prov-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-9 px-3 text-sm bg-white border border-zinc-200 rounded-sm focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 outline-none">
                  <option value="tevau">tevau</option>
                  <option value="sandbox">sandbox</option>
                </select>
              </div>
              <Field label="Base URL" testId="prov-base-url" value={form.base_url} onChange={(v) => setForm({ ...form, base_url: v })} />
              <Field label="API key (write only)" testId="prov-api-key" value={form.api_key} onChange={(v) => setForm({ ...form, api_key: v })} />
              <Field label="API secret (write only)" testId="prov-api-secret" value={form.api_secret} onChange={(v) => setForm({ ...form, api_secret: v })} />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="prov-enabled" />
                Enable provider
              </label>
              <Field label="Notes" testId="prov-notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="h-9 px-4 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-100 transition-colors duration-150">Cancel</button>
                <button data-testid="prov-save-btn" className="h-9 px-4 text-sm bg-zinc-950 text-white rounded-sm hover:bg-zinc-800 transition-colors duration-150">Save</button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </Layout>
  );
}
